import { useEffect, useState, useCallback } from "react";
import { api, formatNumber, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function DataEntryPage() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [itemKey, setItemKey] = useState("");
  const [subItemKey, setSubItemKey] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [quantity, setQuantity] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await api.get("/entries", { params: { limit: 10 } });
      setRecent(data);
    } catch (err) {
      console.error("Son kayıtlar yüklenemedi", err);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    try {
      const { data } = await api.get("/items/catalog");
      setCatalog(data);
    } catch {
      toast.error("Katalog yüklenemedi");
    }
  }, []);

  useEffect(() => {
    loadCatalog();
    loadRecent();
  }, [loadCatalog, loadRecent]);

  const selectedItem = catalog.find((c) => c.key === itemKey);
  const selectedSub = selectedItem?.sub_items.find((s) => s.key === subItemKey);
  const itemGroups = selectedItem?.groups || [];
  const hasGroups = itemGroups.length > 0;

  const canSubmit = user?.role === "admin" || user?.role === "editor";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!itemKey || !subItemKey || !quantity) {
      toast.error("İmalat, alt kırılım ve miktar zorunludur");
      return;
    }
    if (hasGroups && !groupKey) {
      toast.error("Bu imalat için grup seçmelisiniz");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        item_key: itemKey,
        sub_item_key: subItemKey,
        date,
        quantity: parseFloat(quantity),
        location: location || null,
        note: note || null,
      };
      if (hasGroups) payload.group_key = groupKey;
      await api.post("/entries", payload);
      toast.success("Metraj kaydedildi");
      setQuantity("");
      setLocation("");
      setNote("");
      loadRecent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canSubmit) {
    return (
      <div className="site-card p-8 rounded-lg text-center" data-testid="entry-no-permission">
        <div className="text-red-600 font-black text-xl mb-2">Yetki Yetersiz</div>
        <div className="text-slate-600">Veri girişi için Editör veya Yönetici rolü gerekir. Mevcut rol: <span className="font-bold">{user?.role}</span></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="data-entry-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-sky-500 mb-2">Günlük Veri Girişi</div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight font-display">
          Metraj Kaydı Ekle
        </h1>
        <p className="text-slate-600 mt-2">Sahada yapılan günlük ilerlemeyi buradan kaydedin.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={onSubmit} className="lg:col-span-2 site-card p-6 rounded-lg space-y-5" data-testid="entry-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="font-semibold text-sm text-slate-700">İmalat Kalemi *</Label>
              <Select value={itemKey} onValueChange={(v) => { setItemKey(v); setSubItemKey(""); setGroupKey(""); }}>
                <SelectTrigger className="mt-1.5 h-12 border-2 border-slate-300" data-testid="entry-item-select">
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  {catalog.filter((c) => c.kind !== "dere_ano" && c.key !== "dere").map((c) => (
                    <SelectItem key={c.key} value={c.key} data-testid={`entry-item-option-${c.key}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasGroups && (
              <div>
                <Label className="font-semibold text-sm text-slate-700">Grup *</Label>
                <Select value={groupKey} onValueChange={setGroupKey}>
                  <SelectTrigger className="mt-1.5 h-12 border-2 border-slate-300" data-testid="entry-group-select">
                    <SelectValue placeholder="Grup seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {itemGroups.map((g) => (
                      <SelectItem key={g.key} value={g.key} data-testid={`entry-group-option-${g.key}`}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="font-semibold text-sm text-slate-700">Alt Kırılım *</Label>
              <Select value={subItemKey} onValueChange={setSubItemKey} disabled={!selectedItem}>
                <SelectTrigger className="mt-1.5 h-12 border-2 border-slate-300" data-testid="entry-subitem-select">
                  <SelectValue placeholder={selectedItem ? "Seçiniz" : "Önce imalat seçin"} />
                </SelectTrigger>
                <SelectContent>
                  {selectedItem?.sub_items.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`entry-subitem-option-${s.key}`}>
                      {s.name} ({s.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date" className="font-semibold text-sm text-slate-700">Tarih *</Label>
              <div className="relative mt-1.5">
                <Input
                  id="date"
                  type="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-12 border-2 border-slate-300 pr-10"
                  data-testid="entry-date-input"
                />
                <CalendarIcon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <Label htmlFor="qty" className="font-semibold text-sm text-slate-700">
                Miktar * {selectedSub && <span className="text-sky-500">({selectedSub.unit})</span>}
              </Label>
              <Input
                id="qty"
                type="number"
                step="0.01"
                min="0"
                required
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="220"
                className="mt-1.5 h-12 border-2 border-slate-300 font-mono-num text-lg"
                data-testid="entry-quantity-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="loc" className="font-semibold text-sm text-slate-700">
              Lokasyon / Cadde <span className="text-slate-400 font-normal">(opsiyonel — KM veya nokta bilgisi)</span>
            </Label>
            <Input
              id="loc"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ör: KM 2+150"
              className="mt-1.5 h-12 border-2 border-slate-300"
              data-testid="entry-location-input"
            />
          </div>

          <div>
            <Label htmlFor="note" className="font-semibold text-sm text-slate-700">Not</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Örn: Sabah vardiyası, hava koşulları nedeniyle yarım gün"
              className="mt-1.5 border-2 border-slate-300 min-h-[90px]"
              data-testid="entry-note-input"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto h-12 bg-sky-500 hover:bg-sky-600 text-white font-bold px-8"
            data-testid="entry-submit-button"
          >
            {submitting ? <Loader2 className="animate-spin" /> : <><Save size={16} className="mr-2" /> Metrajı Kaydet</>}
          </Button>
        </form>

        {/* Recent entries side panel */}
        <div className="site-card p-6 rounded-lg" data-testid="recent-entries">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-700 mb-4">Son 10 Kayıt</h3>
          {recent.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Henüz kayıt yok</div>
          ) : (
            <ul className="space-y-3">
              {recent.map((e) => {
                const it = catalog.find((c) => c.key === e.item_key);
                const sub = it?.sub_items.find((s) => s.key === e.sub_item_key);
                const grp = e.group_key ? (it?.groups || []).find((g) => g.key === e.group_key) : null;
                return (
                  <li key={e.id} className="border-l-4 border-sky-500 pl-3 py-1" data-testid={`recent-entry-${e.id}`}>
                    <div className="text-xs text-slate-500">{e.date}</div>
                    <div className="text-sm font-bold text-slate-900">
                      {it?.name}{grp && <span className="text-sky-700"> · {grp.name}</span>} — {sub?.name}
                    </div>
                    <div className="text-sm font-mono-num text-sky-600 font-bold">
                      {formatNumber(e.quantity)} {sub?.unit}
                    </div>
                    {e.location && <div className="text-xs text-slate-500">📍 {e.location}</div>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
